import { Component } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import { PublishingState } from "@/generated/types";
import { BSpinner } from "@/lib/typed-bootstrap";
import { GraphapiModule } from "@/store/modules/graphapi";
import ApiPublishedTypes from "../api-management/ApiPublishedTypes.mixin";

import {
  Button,
  ButtonShapeEnum,
  ButtonSizeEnum,
  ButtonThemeEnum,
} from "../shared/Button.component";
import styles from "./UrlWidget.component.module.scss";

@Component
export default class UrlWidget extends ApiPublishedTypes {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  publishingRegex = /PUBLISHING/;

  $refs!: {
    graphqlUrl: HTMLElement;
  };

  get apiState() {
    return GraphapiModule.states[this.apiData?.id ?? ""] ?? this.apiData?.state;
  }

  get isUnpublished(): boolean {
    return !this.apiData?.graphqlUrl;
  }

  get unpublishedText(): string {
    return this.$t("url_widget_unpublished_changes_hint").toString();
  }

  get publishingFailed(): boolean {
    return this.apiState === PublishingState.PUBLISHING_FAILED;
  }

  get isPublishing(): boolean {
    return (
      !this.publishingFailed &&
      this.apiState?.match(this.publishingRegex) !== null
    );
  }

  get graphqlUrl(): string {
    return this.apiData?.graphqlUrl ?? "";
  }

  copyUrl() {
    try {
      navigator.clipboard.writeText(this.$refs.graphqlUrl.innerText);
    } catch (err) {
      console.log((err as Error).message);
    }
  }

  async publishApi() {
    this.handlePublishClick();
  }

  viewPublishingProgress() {
    try {
      this.$router.push({
        name: "project-setup-publishing",
        params: { apiId: this.apiData?.id ?? "" },
      });
    } catch (err) {
      console.log(err);
    }
  }

  render() {
    return (
      <div
        class={{
          [styles.urlWidget]: true,
          failed: this.publishingFailed,
        }}
      >
        {this.graphqlUrl ? (
          <div>
            {/* Main URL + Copy Button */}
            <div class={styles.urlWidgetSection}>
              <div class="api-url--wrap">
                <strong class={styles.urlWidgetCaption}>
                  <span class="mobile">{this.$t("global_url")}</span>
                  <span class="desktop">{this.$t("global_graphql_url")}</span>
                </strong>
                <span ref="graphqlUrl">{this.graphqlUrl}</span>
              </div>

              <Button
                class={styles.actionButton}
                shape={ButtonShapeEnum.square}
                theme={ButtonThemeEnum.outline}
                size={ButtonSizeEnum.sm}
                onClick={this.copyUrl}
                data-testid="publishApiButton"
              >
                <div class="mobile">{this.$t("global_cta_copy")}</div>
                <div class="desktop">
                  {this.$t("global_cta_copy_clipboard")}
                </div>
              </Button>
            </div>

            {/* Publishing Failed */}
            {this.publishingFailed && (
              <div class={styles.republishAlertSection}>
                <div>
                  <strong>{this.$t("url_widget_publish_failed")}</strong>{" "}
                  {this.$t("url_widget_publish_failed_hint")}
                </div>
                <button
                  class="copy-button--pill failed"
                  onClick={this.publishApi}
                >
                  <div>{this.$t("url_widget_publish_cta_repbulish")}</div>
                </button>
              </div>
            )}

            {/* Publishing in Progress */}
            {!this.publishingFailed && this.isPublishing && (
              <div class={styles.republishAlertSection}>
                <div class="text-and-spinner">
                  <div class="default__spinner--small" role="status">
                    <BSpinner
                      class="ml-auto"
                      type="grow"
                      label="Publishing..."
                      small
                    />
                  </div>
                  <div class="button__text">
                    {this.$t("url_widget_publish_progress")}
                  </div>
                </div>
                <Button
                  class={styles.actionButton}
                  shape={ButtonShapeEnum.square}
                  theme={ButtonThemeEnum.outline}
                  size={ButtonSizeEnum.sm}
                  onClick={this.viewPublishingProgress}
                  data-testid="publishApiButton"
                >
                  {this.$t("url_widget_publish_cta_progress")}
                </Button>
              </div>
            )}

            {/* Has Unpublished Changes */}
            {!this.publishingFailed &&
              !this.isPublishing &&
              this.hasUnpublishedChanges && (
                <div class={styles.republishAlertSection}>
                  <div>
                    <strong>{this.$t("url_widget_unpublished_changes")}</strong>{" "}
                    <span>{this.unpublishedText}</span>
                  </div>
                  <Button
                    class={styles.actionButton}
                    shape={ButtonShapeEnum.square}
                    theme={ButtonThemeEnum.outline}
                    size={ButtonSizeEnum.sm}
                    onClick={this.publishApi}
                    data-testid="publishApiButton"
                  >
                    {this.$t("url_widget_publish_cta_repbulish")}
                  </Button>
                </div>
              )}
          </div>
        ) : this.isUnpublished ? (
          <div class={styles.urlWidgetSection}>
            <div class="api-url--wrap">
              {this.$t("url_widget_publish_hint")}
            </div>
            <Button
              class={styles.actionButton}
              shape={ButtonShapeEnum.square}
              theme={ButtonThemeEnum.outline}
              size={ButtonSizeEnum.sm}
              onClick={this.publishApi}
              data-testid="publishApiButton"
            >
              {this.$t("global_cta_publish")}
            </Button>
          </div>
        ) : this.isPublishing ? (
          <div class={styles.urlWidgetSection}>
            <div class="text-and-spinner">
              <div class="default__spinner--small" role="status">
                <BSpinner
                  class="ml-auto"
                  type="grow"
                  label="Publishing..."
                  small
                />
              </div>
              <div class="button__text">
                {this.$t("url_widget_publish_progress")}
              </div>
            </div>
            <Button
              class={styles.actionButton}
              shape={ButtonShapeEnum.square}
              theme={ButtonThemeEnum.outline}
              size={ButtonSizeEnum.sm}
              onClick={this.viewPublishingProgress}
            >
              {this.$t("url_widget_publish_cta_progress")}
            </Button>
          </div>
        ) : this.publishingFailed ? (
          <div class={styles.urlWidgetSection}>
            <div class="api-url--wrap">
              <span class="failed">
                <strong>{this.$t("url_widget_publish_failed")}</strong>
              </span>
              {this.$t("url_widget_publish_failed_hint")}
            </div>
            <button
              class={{
                "copy-button--pill": true,
                failed: this.publishingFailed,
              }}
              onClick={this.publishApi}
            >
              <div class="button__text">
                {this.$t("url_widget_publish_cta_repbulish")}
              </div>
            </button>
          </div>
        ) : null}
      </div>
    );
  }
}
